import type React from 'react';
import { FormattedMessage } from 'react-intl';

import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { SGapCol, XsGapCol } from '@xing-com/crate-jobs-components-columns';
import {
  JobTeaserFacts,
  useAriaLabel,
} from '@xing-com/crate-jobs-components-job-teaser-options';
import { MultiLocationDisplay } from '@xing-com/crate-jobs-components-multi-location-display';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import { useJobTeaserClickExternalTracking } from '@xing-com/crate-jobs-hooks';
import type { JobTeaserListItemProps } from '@xing-com/crate-jobs-xinglets';

import { Highlight } from './highlight';
import * as S from './job-teaser-list-item.styles';

export const JobTeaserListItem: React.FC<JobTeaserListItemProps> = ({
  job,
  isNew,
  url,
  matchingHighlights,
  className,
  onClick: clickCallback,
  onContextMenu,
  onAuxClick,
  actions,
  'data-testid': dataTestId,
  headingLevel = 'h3',
  showEmploymentType,
  'aria-label': ariaLabel,
  highlight,
  isUrgentJob,
}) => {
  const {
    id,
    refreshedAt,
    title,
    locations,
    companyName,
    companyLogo,
    redirectsToThirdPartyUrl,
    applyUrl,
  } = selectJobData(job);

  const ariaLabelText = useAriaLabel({
    ariaLabel: ariaLabel ?? title,
    redirectsToThirdPartyUrl,
    target: '_blank',
  });
  const onClick = useJobTeaserClickExternalTracking({
    isExternalJob: redirectsToThirdPartyUrl,
    jobId: id,
    url,
    clickCallback,
  });

  return (
    <S.Card
      forwardedAs="article"
      href={redirectsToThirdPartyUrl && applyUrl ? applyUrl : url}
      linkProps={{
        'aria-label': ariaLabel,
        target: '_blank',
        onClick,
        onContextMenu,
        onAuxClick,
        rel: redirectsToThirdPartyUrl
          ? 'noopener nofollow sponsored'
          : undefined,
      }}
      data-testid={dataTestId}
      className={className}
      aria-label={ariaLabelText}
      $urgentlyNeeded={isUrgentJob}
    >
      <S.Image
        type="company"
        profileName={companyName}
        size="small"
        sizeConfined="medium"
        src={companyLogo ?? ''}
        // @ts-expect-error FIXME: SC6
        title={null}
        alt=""
        aria-hidden
      />
      <S.Content>
        <S.Header>
          <XsGapCol>
            {isUrgentJob ? (
              <S.Marker variant="strong">
                <FormattedMessage id="JOBS_HIGHLIGHT_URGENTLY_NEEDED" />
              </S.Marker>
            ) : redirectsToThirdPartyUrl ? (
              <S.Marker variant="default">
                <AriaExtendedText
                  item={{ id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT' }}
                  a11yItem={{
                    id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT_A11Y',
                  }}
                />
              </S.Marker>
            ) : matchingHighlights?.highlight ? (
              <S.Marker variant="highlight">
                {matchingHighlights?.highlight?.localization.localizationValue}
              </S.Marker>
            ) : null}
            <S.MainInfo>
              <S.Title
                forwardedAs={headingLevel}
                data-testid="job-teaser-list-title"
                noMargin
              >
                {title}
              </S.Title>
              <S.Company noMargin size="small">
                {companyName}
              </S.Company>
              <MultiLocationDisplay locations={locations} size="small" />
            </S.MainInfo>
          </XsGapCol>
        </S.Header>
        <SGapCol>
          <JobTeaserFacts
            job={job}
            matchingHighlights={matchingHighlights}
            showEmploymentType={showEmploymentType}
          />
          <Highlight highlight={highlight} />
          {refreshedAt ? (
            <S.PublicationDate date={refreshedAt} isNew={isNew} />
          ) : null}
        </SGapCol>
      </S.Content>
      {actions}
    </S.Card>
  );
};
